import React, { useState } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button, Divider } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { toast } from 'react-hot-toast';
import { CreateCourse } from 'services/course/createCourse';

const CreateCourseDialogue = ({ open, onClose, title }) => {
  const [courseName, setcourseName] = useState('');
  const [postureCount, setposturenumber] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [sequenceType, setSequenceType] = useState('');

  // validation state
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const resetForm = () => {
    setcourseName('');
    setposturenumber('');
    setDifficulty('');
    setCategory('');
    setDescription('');
    setTags('');
    setSequenceType('');
    setErrors({});
  };

  const { mutate, isLoading } = useMutation({
    mutationFn: CreateCourse,
    onSuccess: (response) => {
      toast.success(response.data.message || 'Course Created Successfully');
      resetForm();
      queryClient.invalidateQueries(['allPoses']);
      if (onClose) onClose();
      navigate('/instructor');
    },
    onError: (error) => {
      toast.error('Failed to create course: ' + (error.message || 'Unknown error'));
      console.error('Failed to create course:', error);
    }
  });

  const validateForm = () => {
    let tempErrors = {};
    if (!courseName) tempErrors.courseName = 'Course name is required';
    if (!postureCount) tempErrors.postureCount = 'Number of postures is required';
    if (!difficulty) tempErrors.difficulty = 'Difficulty is required';
    if (!sequenceType) tempErrors.sequenceType = 'Sequence type is required';
    if (!category) tempErrors.category = 'Category is required';
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleCreateCourse = () => {
    if (!validateForm()) {
      toast.error('Please fill the required fields');
      return;
    }

    const newCourse = {
      courseName,
      asanaCount: Number(postureCount),
      difficulty,
      category,
      description,
      tags: tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag),
      sequenceType,
      coachId: localStorage.getItem('userid')
    };

    mutate(newCourse);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ padding: 3, display: 'flex', justifyContent: 'left' }}>
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            <TextField
              label="Course Name"
              variant="outlined"
              value={courseName}
              onChange={(e) => setcourseName(e.target.value)}
              error={!!errors.courseName}
              helperText={errors.courseName}
              sx={{ mb: 2, width: '100%' }}
            />
            <TextField
              label="Number of Postures"
              variant="outlined"
              type="number"
              value={postureCount}
              onChange={(e) => setposturenumber(e.target.value)}
              error={!!errors.postureCount}
              helperText={errors.postureCount}
              sx={{ mb: 2, width: '100%' }}
            />

            <TextField
              select
              SelectProps={{ native: true }}
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              error={!!errors.difficulty}
              helperText={errors.difficulty}
              sx={{ mb: 2, width: '100%' }}
            >
              <option value="" disabled>
                Select Difficulty
              </option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </TextField>

            <TextField
              select
              SelectProps={{ native: true }}
              value={sequenceType}
              onChange={(e) => setSequenceType(e.target.value)}
              error={!!errors.sequenceType}
              helperText={errors.sequenceType}
              sx={{ mb: 2, width: '100%' }}
            >
              <option value="" disabled>
                Sequence Type
              </option>
              <option value="standalone">Stand Alone</option>
              <option value="continuous">Continuous</option>
            </TextField>

            <TextField
              label="Category"
              variant="outlined"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              error={!!errors.category}
              helperText={errors.category}
              sx={{ mb: 2, width: '100%' }}
            />

            <TextField
              label="Description"
              variant="outlined"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mb: 2, width: '100%' }}
            />

            <TextField
              label="Tags (comma separated)"
              variant="outlined"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              sx={{ mb: 2, width: '100%' }}
            />

            <DialogActions>
              <Button onClick={handleCreateCourse} variant="contained" disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Create'}
              </Button>
              <Button onClick={onClose} variant="outlined">
                Cancel
              </Button>
            </DialogActions>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCourseDialogue;
